import { tearDown } from '@disy/lib/teardown';

import { isKnownSourceOrigin, subscribeToEvent } from 'cadenza/integration/post-message';
import { isExternal } from 'cadenza/utils/cadenza-url/cadenza-url';

const KNOWN_WINDOW_NAMES = ['_self', '_blank', '_parent', '_top'];
const WINDOW_CLOSED_POLL_INTERVAL = 500;

/**
 * Opens a URL in the given target window.
 *
 * This is intended to be a safe wrapper around `window.open()`:
 * - Typed options are used instead of a `windowFeatures` string.
 * - Access from the target window to the Cadenza window is allowed only under these conditions:
 *   - For a target window with a custom name, because re-using a named window does not work otherwise.
 *   - If the URL is a known source origin (see [`isKnownSourceOrigin()`]{@link import('cadenza/integration/post-message').isKnownSourceOrigin}),
 *     so that the target window can post messages to the Cadenza window.
 * - Does not return the target window, because `postMessage()` should be used to communicate with
 *   other windows.
 *
 * @param url - URL to open
 * @param [options]
 * @param [options.target='_blank'] - Name of the target window
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/open|Window.open() on MDN}
 */
export function openUrl(
  url: string,
  {
    target = '_blank',
  }: {
    target?: string;
  } = {},
) {
  const noopener = isExternal(url) && !isKnownSourceOrigin(url) && KNOWN_WINDOW_NAMES.includes(target);
  setTimeout(() => {
    // eslint-disable-next-line no-restricted-syntax
    const childWindow = window.open(url, target, noopener ? 'noopener' : '');
    if (childWindow && !noopener) {
      setupPostMessageTarget(childWindow);
    }
  });
}

function setupPostMessageTarget(childWindow: Window) {
  const teardown = subscribeToEvent('closeMe', () => childWindow.close(), childWindow);
  pollWindowClosed(childWindow, () => tearDown(teardown));
}

function pollWindowClosed(childWindow: Window, onClose: () => void) {
  const timer = setInterval(checkWindowClosed, WINDOW_CLOSED_POLL_INTERVAL);
  function checkWindowClosed() {
    if (childWindow.closed) {
      onClose();
      clearInterval(timer);
    }
  }
}
